// Surface 02 — le suivi des candidatures.
//
// Ce que la console ne savait pas faire du tout. « Marc est proposé chez ACME,
// envoyé mardi, toujours sans réponse » n'existait nulle part : le seul état
// d'une offre était déduit du dernier appel IA, donc générer un CV valait
// « dossier généré » et cliquer une fois dans ce dossier valait « positionné ».
// Deux états pour tout un cycle commercial, tous deux écrits par des gestes qui
// n'étaient pas des décisions.
//
// PAS DE FILTRES, comme sur la pile, et pour la même raison : la page ne montre
// qu'une chose. Les étapes sont des SECTIONS, pas des choix à poser — on voit
// tout le pipeline d'un coup d'œil et on lit ce qui dort.
//
// LES CLOSES SONT REPLIÉES EN BAS, jamais effacées : « placé » est le seul
// chiffre qui compte à la fin du trimestre, et un refus dit pourquoi une offre
// ne reviendra pas.

import { useEffect, useState } from 'react'
import { premiereFoisCetteSession } from '../lib/mouvement'
import { ChevronDown, ChevronRight, Copy, ExternalLink, FileText, Clock3, Undo2, X, ArrowRight, Link2, Plus } from 'lucide-react'
import { get, post, type OfferRow, type SuiviRow, type Summary } from '../lib/api'
import { relTime, initials, teinte, estClose, jourCourt, fullDate, parseDate } from '../lib/format'
import { prochaineEtape, estExpireeNonEnvoyee, dateRappel, type ProchaineEtape } from '../lib/prochaineEtape'
import { listePreuve } from '../lib/preuveRecherche'
import { navigate } from '../lib/router'
import { useData, Loading, Empty, Score, ActionButton, useToast } from '../components/common'
import { SelecteurEtape, ETAPES } from '../components/candidature'
import { useMots } from '../lib/vocabulaire'
import { useCandidatureStatus } from '../lib/dictionnaires'
import { useProfilUnique } from '../lib/formule'
import { useT } from '../lib/i18n'
import { Employeur, LogoEntreprise } from '../components/sites'
import { Carte, type CarteOffre } from './Accueil'
import { SaisieManuelle } from './Veille'
import { RechercheOffres, TriRapide } from '../components/triRapide'

/** Au-delà de ça, une candidature sans relance prévue (un entretien qui ne
 *  donne pas de nouvelles) n'avance plus : on le dit. Les envoyées ont leur
 *  propre rappel, à une semaine (`JOURS_AVANT_RELANCE`). */
const JOURS_AVANT_RAPPEL = 10

export function Suivi({ onChanged }: { summary: Summary | null; onChanged: () => void }) {
  // Une fois par session, comme l'accueil (#183).
  const [animer, setAnimer] = useState(() => premiereFoisCetteSession('mesoffres'))
  const ETAT = useCandidatureStatus()
  const mots = useMots()
  const t = useT()
  const candidatures = useData(() => get<SuiviRow[]>('/api/candidatures'), [])
  const aRevoir = useData(() => get<OfferRow[]>('/api/offers?scope=later'), [])
  // Une fois la première liste jouée, les lignes montées ensuite (statut
  // changé, rechargement) ne rejouent rien.
  const chargee = !!candidatures.data
  useEffect(() => {
    if (!animer || !chargee) return
    const fin = setTimeout(() => setAnimer(false), 2500)
    return () => clearTimeout(fin)
  }, [animer, chargee])
  // Radar (lot 2) : les offres gardées depuis l'extension ou ajoutées à la
  // main, pas encore tranchées. Elles n'ont plus de pile où attendre : « Mes
  // offres » est leur seule place, en tête.
  const profilUnique = useProfilUnique()
  const aDecider = useData(() => (profilUnique ? get<CarteOffre[]>('/api/radar/a-decider') : Promise.resolve([])), [profilUnique])
  const [closesOuvertes, setClosesOuvertes] = useState(false)
  const [expireesOuvertes, setExpireesOuvertes] = useState(false)
  const toast = useToast()

  function rafraichir() {
    candidatures.reload()
    aRevoir.reload()
    aDecider.reload()
    onChanged()
  }

  if (candidatures.loading) return <Loading label={t('suivi.loading')} />
  if (candidatures.error) return <Empty>{candidatures.error}</Empty>

  const toutes = candidatures.data ?? []
  // Une offre disparue avant l'envoi n'attend plus rien de nous : elle quitte
  // seule le pipeline pour un repli, au lieu de rester « à envoyer » à jamais.
  const expirees = toutes.filter(estExpireeNonEnvoyee)
  const actives = toutes.filter((k) => !estClose(k.status) && !estExpireeNonEnvoyee(k))
  const closes = toutes.filter((k) => estClose(k.status))
  const reserve = aRevoir.data ?? []
  const apportees = aDecider.data ?? []

  // La réserve « à revoir » : des offres, pas des candidatures. Elle vit ici
  // parce que c'est le même geste de suivi (« qu'est-ce qui m'attend ? »). En
  // Radar elle passe en tête, avec les offres gardées depuis l'extension :
  // c'est ce qui attend une décision (lot 3).
  const blocReserve = (
    reserve.length > 0 && (
              <section className="ksec">
                <div className="ksec-h">
                  <span className="kd k-later" />
                  <b>{mots('suivi.reserve')}</b>
                  <span className="n">{reserve.length}</span>
                  <span className="ksec-a">{mots('suivi.reserveAide')}</span>
                </div>
                {reserve.map((o) => (
                  <div className="krow" key={o.id}>
                    <button className="krow-main" onClick={() => navigate(`#/offre/${o.id}`)}>
                      <LogoEntreprise nom={o.company} logo={o.logo} />
                      <span className="pc-id">
                        <span className="pc-t">{o.title}</span>
                        <span className="pc-c">
                          <Employeur nom={o.company} source={o.source_name} /> · {t('suivi.setAsideAt', { when: relTime(o.status_at ?? o.first_seen_at) })}
                          {profilUnique && o.status_at && ` · ${t('suivi.rappelLe', { date: fullDate(dateRappel(parseDate(o.status_at) ?? new Date()).toISOString()) })}`}
                        </span>
                      </span>
                    </button>
                    {o.matches.length > 0 && <Score value={Math.max(...o.matches.map((m) => m.score))} anime={animer} />}
                    <ActionButton
                      size="sm"
                      title={t('suivi.putBackTitle', { name: mots('nav.decisions') })}
                      onClick={async () => {
                        await post(`/api/offers/${o.id}/status`, { status: 'todo' })
                        toast(t('suivi.putBackToast', { title: o.title, name: mots('nav.decisions') }))
                        rafraichir()
                      }}
                    >
                      <Undo2 size={13} />{mots('suivi.remettre')}
                    </ActionButton>
                  </div>
                ))}
              </section>
            )
  )

  return (
    <>
      <div className="bar">
        <div>
          <h3>{mots('suivi.titre')}</h3>
          <div className="sub">
            {actives.length === 0
              ? mots('suivi.vide')
              : t(actives.length > 1 ? 'suivi.activeCountPlural' : 'suivi.activeCountSingular', { n: actives.length })}
            {closes.length > 0 &&
              ` · ${closes.filter((k) => k.status === 'place').length} ${mots('suivi.placementsCount')}`}
          </div>
        </div>
      </div>

      <div className="body">
        {profilUnique && <RechercheOffres />}
        {profilUnique && <TriRapide onChanged={rafraichir} />}
        {apportees.length > 0 && (
          <section className="acc-sec">
            <div className="acc-entete">
              <span className="acc-eyebrow">{t('suivi.aDecider')}</span>
              <span className="field-help">{t('suivi.aDeciderAide')}</span>
            </div>
            <div className="acc-cartes">
              {apportees.map((o) => <Carte key={o.id} o={o} sous={t('suivi.gardeeLe', { date: fullDate(o.vueLe) })} />)}
            </div>
          </section>
        )}
        {profilUnique && blocReserve}
        {profilUnique && <AjoutParLien onDone={rafraichir} />}
        {toutes.length === 0 && reserve.length === 0 && apportees.length === 0 ? (
          <Empty>
            {mots('suivi.vide')}. {mots('suivi.naissance')}
            <button className="linkbtn" onClick={() => navigate('#/decisions')}>{mots('nav.decisions')}</button>.
          </Empty>
        ) : (
          <>
            {/* Les étapes actives, dans l'ordre du pipeline. Une étape vide
                n'affiche rien : une colonne à zéro occupe la place d'une
                colonne pleine et fait paraître le pipeline plus mou qu'il
                n'est. */}
            {ETAPES.filter((s) => !estClose(s)).map((s) => {
              const lot = actives.filter((k) => k.status === s)
              if (lot.length === 0) return null
              return (
                <section className="ksec" key={s}>
                  <div className="ksec-h">
                    <span className={`kd ${ETAT[s].cls}`} />
                    <b>{ETAT[s].label}</b>
                    <span className="n">{lot.length}</span>
                    <span className="ksec-a">{ETAT[s].aide}</span>
                  </div>
                  {lot.map((k) => <Ligne key={k.id} k={k} onChanged={rafraichir} anime={animer} />)}
                </section>
              )
            })}

            {!profilUnique && blocReserve}

            {expirees.length > 0 && (
              <section className="ksec closed">
                <button
                  className="ksec-h as-btn"
                  aria-expanded={expireesOuvertes}
                  onClick={() => setExpireesOuvertes((v) => !v)}
                >
                  {expireesOuvertes ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <b>{t('suivi.expiredTitle')}</b>
                  <span className="n">{expirees.length}</span>
                  <span className="ksec-a">{t('suivi.expiredHint')}</span>
                </button>
                {expireesOuvertes && expirees.map((k) => <Ligne key={k.id} k={k} onChanged={rafraichir} />)}
              </section>
            )}

            {closes.length > 0 && (
              <section className="ksec closed">
                <button className="ksec-h as-btn" onClick={() => setClosesOuvertes((v) => !v)}>
                  {closesOuvertes ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <b>{mots('suivi.closes')}</b>
                  <span className="n">{closes.length}</span>
                  <span className="ksec-a">
                    {t('suivi.closedSummary', {
                      placed: closes.filter((k) => k.status === 'place').length,
                      placedLabel: mots('suivi.placementsCount'),
                      refused: closes.filter((k) => k.status === 'refuse').length,
                    })}
                  </span>
                </button>
                {closesOuvertes && closes.map((k) => <Ligne key={k.id} k={k} onChanged={rafraichir} />)}
              </section>
            )}

            {/* La preuve de recherche (issue #170) : Radar seulement, un
                particulier qui doit dire quand il a postulé, et où. */}
            {profilUnique && <PreuveRecherche ks={toutes} />}
          </>
        )}
      </div>
    </>
  )
}

function Ligne({ k, onChanged, anime = false }: { k: SuiviRow; onChanged: () => void; anime?: boolean }) {
  const mots = useMots()
  const t = useT()
  const profilUnique = useProfilUnique()
  const toast = useToast()
  const [confirmer, setConfirmer] = useState(false)
  const jours = Math.floor((Date.now() - new Date(k.updated_at.replace(' ', 'T') + 'Z').getTime()) / 86400_000)
  const expiree = estExpireeNonEnvoyee(k)
  const suite = expiree ? null : prochaineEtape(k)
  // Le signal qui manquait le plus : un dossier envoyé il y a trois semaines a
  // exactement la même apparence qu'un envoyé ce matin. Les envoyées le disent
  // par leur date de relance ; « dort » couvre ce qui n'en a pas.
  const dort = !estClose(k.status) && k.status !== 'a_envoyer' && suite?.cle !== 'relancer' && jours >= JOURS_AVANT_RAPPEL
  const relanceDue = suite?.cle === 'relancer' && suite.enRetard

  return (
    <div className={`krow${dort || relanceDue ? ' dort' : ''}`}>
      <button className="krow-main" onClick={() => navigate(`#/offre/${k.offerId}`)}>
        {/* QUI VERS QUI, ou QUOI. Un business manager suit quinze personnes,
            donc la ligne part du nom : c'est ce qui distingue deux candidatures
            chez le même employeur. Un particulier n'est jamais que lui-même, et
            son propre nom répété sur chaque ligne pousse hors du champ le seul
            élément qui les sépare, l'intitulé du poste. */}
        {profilUnique ? (
          <LogoEntreprise nom={k.company} logo={k.logo} className="miniav" />
        ) : (
          <span className="miniav" style={teinte(k.name)}>{initials(k.name)}</span>
        )}
        <span className="pc-id">
          <span className="pc-t">
            {profilUnique ? (
              k.title
            ) : (
              <>
                {k.name}
                <span className="krow-sep">→</span>
                <span className="krow-co"><Employeur nom={k.company} source={k.source_name} /></span>
              </>
            )}
          </span>
          <span className="pc-c">
            {profilUnique ? <Employeur nom={k.company} source={k.source_name} /> : k.title}
            {k.offerArchived && <span className="krow-gone" title={t('suivi.offerGoneTitle')}> · {t('suivi.offerExpired')}</span>}
          </span>
        </span>
      </button>

      {suite && <EtapeSuivante suite={suite} />}

      <div className="krow-acts">
      {dort && (
        <span className="krow-old" title={t('suivi.noMovementTitle', { n: jours })}>
          <Clock3 size={12} />{t('suivi.daysShort', { n: jours })}
        </span>
      )}
      {k.score !== null && <Score value={k.score} anime={anime} />}

      {k.dossierId ? (
        <button
          className="chip"
          title={t('suivi.openFileTitle', { name: mots('dossier.nom') })}
          onClick={() => navigate(`#/offre/${k.offerId}/dossier/${k.dossierId}`)}
        >
          <FileText size={12} />{mots('dossier.court')}
        </button>
      ) : (
        <span className="krow-nodoc" title={t('suivi.noFileTitle')}>
          {mots('suivi.sansDossier')}
        </span>
      )}

      {k.offerUrl && (
        <a className="pc-ext" href={k.offerUrl} target="_blank" rel="noopener" title={t('suivi.originalPosting')}>
          <ExternalLink size={13} />
        </a>
      )}

      {/* L'étape et l'abandon voyagent ensemble : sur mobile, c'est entre eux
          et le reste que la ligne se coupe, jamais avant « Abandonner » seul. */}
      <span className="krow-fin">
      {/* Une offre disparue avant l'envoi n'a plus d'étape à choisir : le
          sélecteur proposait encore « À envoyer ». Reste à la retirer. */}
      {!expiree && <SelecteurEtape candidatureId={k.id} status={k.status} onChanged={onChanged} />}

      {/* Discret à dessein : c'est l'action la moins fréquente de la ligne, et
          la seule qui fasse disparaître quelque chose. Elle porte un mot, et
          se confirme dans la ligne : une croix seule ne disait pas ce qu'elle
          faisait, et un clic de trop retirait la candidature. Archiver,
          jamais effacer. */}
      {confirmer ? (
        <span className="krow-confirm" role="group" aria-label={t('suivi.abandonConfirm')}>
          <span>{t('suivi.abandonConfirm')}</span>
          <button
            className="btn-danger btn-sm"
            onClick={async () => {
              await post(`/api/candidatures/${k.id}/archive`, { archived: true })
              toast(t(profilUnique ? 'suivi.removedToastTitle' : 'suivi.removedToastName', profilUnique ? { title: k.title } : { name: k.name }))
              onChanged()
            }}
          >
            {t('suivi.abandon')}
          </button>
          <button className="btn-secondary btn-sm" onClick={() => setConfirmer(false)}>{t('suivi.abandonCancel')}</button>
        </span>
      ) : (
        <button
          className="krow-drop"
          title={mots('suivi.retirer')}
          aria-label={t(profilUnique ? 'suivi.removeAriaTitle' : 'suivi.removeAriaName', profilUnique ? { title: k.title } : { name: k.name })}
          onClick={() => setConfirmer(true)}
        >
          <X size={14} /><span>{t('suivi.abandon')}</span>
        </button>
      )}
      </span>
      </div>
    </div>
  )
}

/** La prochaine étape, en clair : ce qu'on fait, et quand pour une relance. */
function EtapeSuivante({ suite }: { suite: ProchaineEtape }) {
  const t = useT()
  const texte =
    suite.cle === 'relancer'
      ? t(suite.enRetard ? 'suivi.nextFollowUpDue' : 'suivi.nextFollowUp', { date: jourCourt(suite.date) })
      : t(suite.cle === 'envoyer' ? 'suivi.nextSend' : 'suivi.nextInterview')
  const due = suite.cle === 'relancer' && suite.enRetard
  return (
    <span className={`krow-next${due ? ' due' : ''}`} data-testid="prochaine-etape">
      <ArrowRight size={12} />{texte}
    </span>
  )
}

/**
 * « Ajouter une offre par son lien » (Radar, lot 3). Radar lit l'annonce de la
 * page ; s'il ne sait pas (LinkedIn derrière sa connexion), la saisie à la
 * main s'ouvre avec l'adresse déjà remplie.
 */
function AjoutParLien({ onDone }: { onDone: () => void }) {
  const t = useT()
  const toast = useToast()
  const [url, setUrl] = useState('')
  const [manuelle, setManuelle] = useState<string | null>(null)
  return (
    <>
      <div className="ajout-lien">
        <Link2 size={15} />
        <input
          className="grow"
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder={t('suivi.lienPlaceholder')}
          aria-label={t('suivi.lienAria')}
        />
        <ActionButton
          size="sm"
          variant="primary"
          disabled={!/^https?:\/\//.test(url.trim())}
          busyLabel={t('suivi.lienLecture')}
          onClick={async () => {
            try {
              const r = await post<{ id: number }>('/api/offers/par-lien', { url: url.trim() })
              setUrl('')
              onDone()
              navigate(`#/offre/${r.id}`)
            } catch (e: any) {
              if (e?.code !== 'lien_illisible') throw e
              toast(t('suivi.lienIllisible', { raison: e.message }), 'err')
              setManuelle(url.trim())
            }
          }}
        >
          <Plus size={14} />{t('suivi.lienAjouter')}
        </ActionButton>
      </div>
      {manuelle !== null && <SaisieManuelle url={manuelle} onClose={() => setManuelle(null)} onDone={onDone} />}
    </>
  )
}

/** Les candidatures envoyées en une liste à copier, pour le Forem, Actiris ou
 *  le VDAB. Absent tant que rien n'est envoyé. */
function PreuveRecherche({ ks }: { ks: SuiviRow[] }) {
  const t = useT()
  const toast = useToast()
  const texte = listePreuve(ks)
  if (!texte) return null
  const n = texte.split('\n').length
  return (
    <section className="ksec preuve" data-testid="preuve-recherche">
      <div className="ksec-h">
        <b>{t('suivi.preuveTitre')}</b>
        <span className="n">{n}</span>
        <span className="ksec-a">{t('suivi.preuveAide')}</span>
        <button
          type="button"
          className="btn-secondary btn-sm"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(texte)
              toast(t('suivi.preuveCopiee'))
            } catch {
              toast(t('suivi.preuveEchec'), 'err')
            }
          }}
        >
          <Copy size={13} />{t('suivi.preuveCopier')}
        </button>
      </div>
    </section>
  )
}
